"use client";

import { Atom, Beaker, Box, Cpu, Droplet, Flame, Layers, Shield } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Engines() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: gridRef,
    isVisible: gridVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const materials = [{
    name: "Capital Solutions, Engineered.",
    description: "High-leverage capital for rapid acquisition and renovation projects, designed for speed and execution.",
    tensile: "1st Lien",
    temp: "Fast-Close",
    Icon: Layers
  }, {
    name: "BRRRR Strategy",
    description: "Integrated funding for Buy, Rehab, Rent, Refinance, Repeat cycles to scale your portfolio efficiently.",
    tensile: "Bridge",
    temp: "Scalable",
    Icon: Droplet
  }, {
    name: "Buy-and-Hold",
    description: "Long-term debt solutions for stabilized rental assets, optimizing cash flow and debt service coverage.",
    tensile: "Term",
    temp: "Stable",
    Icon: Beaker
  }, {
    name: "New Construction",
    description: "Capital for ground-up development and modular builds, covering land acquisition and vertical construction.",
    tensile: "Dev-Loan",
    temp: "Growth",
    Icon: Box
  }, {
    name: "Gap Equity",
    description: "Second-position capital to bridge the down payment gap, allowing you to close with minimal cash out-of-pocket.",
    tensile: "2nd Lien",
    temp: "Flexible",
    Icon: Atom
  }, {
    name: "JV Partnership",
    description: "Strategic alignment for experienced investors with capital or credit, sharing risk and upside in high-yield deals.",
    tensile: "Equity",
    temp: "Aligned",
    Icon: Shield
  }, {
    name: "Referral Network",
    description: "A robust ecosystem for brokers and professionals to monetize their network through our professional‑grade capital funding.",
    tensile: "Partner",
    temp: "Rewarding",
    Icon: Cpu
  }, {
    name: "Capital Vault",
    description: "Our proprietary capital review process that ensures professional-grade credibility and rapid capital deployment.",
    tensile: "Vault",
    temp: "Secure",
    Icon: Flame
  }];
  return <section data-section-id="2254" id="the-three-engines-of-growth" className="bg-background text-foreground py-20 md:py-28 lg:py-32">
      <div className="mx-auto max-w-7xl px-6 md:px-10 lg:px-16">
        {}
        <div ref={headerRef} className={`grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 mb-16 md:mb-20 transition-all duration-700 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="md:col-span-6">
            <p className="font-mono text-xs md:text-sm tracking-[0.2em] uppercase mb-6" style={{
            color: "var(--gold-accent)"
          }}>
              Capital Infrastructure
            </p>
            <h2 className="font-default font-semibold text-4xl md:text-5xl lg:text-[56px] leading-[1.05] tracking-tight text-foreground">
              Architecting your capital stack.
            </h2>
          </div>
          <div className="md:col-span-6 md:pt-4">
            <p className="font-default text-base md:text-lg leading-relaxed text-slate-muted max-w-xl">
              BeneWealth Financial operates through three distinct engines designed to fund the entire deal, 
              from first mortgage debt to gap equity, ensuring you never have to chase multiple lenders again.
            </p>
          </div>
        </div>

        {}
        <div ref={gridRef} className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 transition-all duration-700 ease-out ${gridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          {materials.map((material, i) => {
          const Icon = material.Icon;
          return <div data-index={i} key={material.name} className="group relative rounded-lg border border-slate-muted/20 bg-card p-7 md:p-8 transition-all duration-300 ease-out hover:-translate-y-1 shadow-md">
                {}
                <div className="mb-7 inline-flex h-[60px] w-[60px] items-center justify-center rounded-lg transition-colors duration-300 bg-slate-muted/10">
                  <Icon className="h-6 w-6 transition-colors duration-300" strokeWidth={1.25} style={{
                color: "var(--gold-accent)"
              }} />
                </div>

                {}
                <h3 className="font-default font-semibold text-[28px] leading-tight text-foreground mb-3">
                  {material.name}
                </h3>

                {}
                <p className="font-default text-sm leading-relaxed text-slate-muted mb-8">
                  {material.description}
                </p>

                {}
                <div className="pt-5 border-t border-slate-muted/20">
                  <p className="font-mono text-xs tracking-wider uppercase">
                    <span style={{
                  color: "var(--gold-accent)"
                }}>TYPE:</span>{" "}
                    <span className="text-foreground">{material.tensile}</span>
                    <span className="mx-2 opacity-50">·</span>
                    <span style={{
                  color: "var(--gold-accent)"
                }}>STATUS:</span>{" "}
                    <span className="text-foreground">{material.temp}</span>
                  </p>
                </div>
              </div>;
        })}
        </div>
      </div>
    </section>;
}